<template>
  <Modal
    :show="show.actionableInsight"
    width="380px"
    action-text="Share"
    hide-default-actions
    @close="close"
  >
    <template #title>
      <h5>Share Report</h5>
    </template>

    <template #content>
      <ShareInput
        :model-value="modelValue"
        :loading="searchLoading"
        :items="autocompleteItems"
        @update:search="handleSearch"
        @update:model-value="handleModelValueUpdate"
      />
      <div v-if="props.selectedJobs.length" class="mt-4">
        <h5 class="selected-jobs-title">
          Selected Jobs
        </h5>
        <ul class="selected-jobs-list">
          <li v-for="job in props.selectedJobs" :key="job.id">
            {{ job.title || job.name || job }}
          </li>
        </ul>
      </div>
      <ShareNote v-if="modelValue?.type_key === TYPE_ACTIVE" v-model="note" class="mt-4" />
      <ShareLeadDetails v-if="modelValue?.type_key === TYPE_LEAD" class="mt-4" />
      <DpplNewButton
        class="w-100 mt-4"
        color="primary"
        size="small"
        :disabled="!modelValue || loading"
        @click="shareViaEmail"
      >
        {{ shareViaEmailText }}
      </DpplNewButton>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import Modal from '@/components/shared/Modal.vue';
import { debounce, startCase } from 'lodash-es';
import { useModals } from '@/stores/modals/modals';
import { useActionableInsights } from '@/stores/insights/actionableInsights';
import ShareInput from '@/components/insights/ShareModal/ShareInput.vue';
import ShareNote from '@/components/insights/ShareModal/ShareNote.vue';
import { searchTeamMembers } from '@/api/asyncSearch';
import { storeToRefs } from 'pinia';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { useRoute } from 'vue-router';
import { useInsightsShare } from '@/stores/insights/share';
import { TYPE_ACTIVE, TYPE_LEAD } from '@/helpers/constants/userType';
import ShareLeadDetails from '@/components/insights/ShareModal/ShareLeadDetails.vue';
import { useTracking } from '@/stores/tracking/tracking';

const props = defineProps({
  selectedJobs: {
    type: Array,
    default: () => [],
  },
});

const modelValue = ref(null);
const autocompleteItems = ref([]);
const searchLoading = ref(false);
const note = ref('');
const loading = ref(false);

const route = useRoute();

const emit = defineEmits(['close']);

const { show } = storeToRefs(useModals());
const { inviteUserToInsights } = useInsightsShare();
const { shareActionableReportViaEmail } = useActionableInsights();

const shareViaEmailText = computed(() => {
  if (loading.value) {
    return 'Sending...';
  }

  return 'Share via Email';
});

const shareUrl = computed(() => {
  const url = new URL(window.location.href);
  url.searchParams.set('share', 'true');

  return url.toString();
});

const debouncedSearch = debounce(async (query) => {
  if (!query) {
    autocompleteItems.value = [];
    return;
  }
  try {
    searchLoading.value = true;
    const response = await searchTeamMembers(query);
    autocompleteItems.value = response.users || [];
  } catch (e) {
    autocompleteItems.value = [];
  } finally {
    searchLoading.value = false;
  }
}, 300);

const handleSearch = (query) => {
  debouncedSearch(query);
};

const handleModelValueUpdate = (value) => {
  modelValue.value = typeof value !== 'string' ? value : null;
};

const shareViaEmail = async () => {
  const isActiveUser = modelValue.value?.type_key === TYPE_ACTIVE;

  loading.value = true;

  if (isActiveUser) {
    await shareActionableReportViaEmail({
      userId: modelValue.value?.id,
      note: note.value,
      selectedJobs: props.selectedJobs,
      reportUrl: shareUrl.value,
      name: `${modelValue.value?.first_name} ${modelValue.value?.last_name || ''}`,
      routeName: route.name,
    });
  } else {
    await inviteUserToInsights({
      email: modelValue.value?.email,
      id: modelValue.value?.id,
      routeName: route.name,
      name: `${modelValue.value?.first_name} ${modelValue.value?.last_name || ''}`,
    });

    // Send five minutes later
    await shareActionableReportViaEmail({
      userId: modelValue.value?.id,
      note: note.value,
      selectedJobs: props.selectedJobs,
      reportUrl: shareUrl.value,
      scheduledAt: 'in 5 min',
      name: `${modelValue.value?.first_name} ${modelValue.value?.last_name || ''}`,
      routeName: route.name,
    });
  }

  useTracking().actionableInsightsShareJobsViaEmail({
    reportName: startCase(route.name),
    sharedUserStatus: modelValue.value?.type_key === TYPE_ACTIVE ? 'Active User' : 'New Invitation',
    noteIncluded: note.value ? 'Yes' : 'No',
  });

  loading.value = false;
  modelValue.value = null;
  note.value = '';

  emit('close');
};

const close = () => {
  emit('close');

  modelValue.value = null;
  note.value = '';
};
</script>

<style scoped lang="less">
:deep(.v-btn--size-small) {
  height: 32px;
}

:deep(.header) {
  border-bottom: 1px solid @stroke-default;
}

.selected-jobs-title {
  font-size: 13px;
  line-height: 150%;
  font-weight: 500;
  color: @text-darkest;
}

.selected-jobs-list {
  max-height: 180px;
  overflow-y: auto;
  list-style-type: none;
  color: @text-light;

  li {
    line-height: 150%;
    font-size: 12px;
    font-weight: 500;
  }
}
</style>
